/**
 * Page-specific SEO metadata for Calgary/Alberta building code assistant
 * Optimized for local SEO and construction industry keywords
 */

export interface PageMeta {
  title: string;
  description: string;
  keywords: string[];
  canonicalPath: string;
  ogType?: "website" | "article";
  structuredDataType?: "WebPage" | "FAQPage" | "WebApplication" | "Article";
}

// Base site info
export const SITE_NAME = "Builder Buddy";
export const SITE_TAGLINE = "AI Building Code Assistant";
export const SITE_URL = "https://builderbuddy.ca";
export const SITE_LOCALE = "en_CA";

// Calgary-specific business info
export const BUSINESS_INFO = {
  name: "Builder Buddy Canada",
  description:
    "AI-powered building code assistant and construction cost estimator for Calgary builders, contractors, developers, and consultants",
  telephone: "+1-403-333-5025",
  address: {
    streetAddress: "",
    addressLocality: "Calgary",
    addressRegion: "AB",
    postalCode: "",
    addressCountry: "CA",
  },
  geo: {
    latitude: 51.0447,
    longitude: -114.0719,
  },
  areaServed: [
    "Calgary",
    "Airdrie",
    "Cochrane",
    "Okotoks",
    "Chestermere",
    "Rocky View County",
    "Alberta",
    "Canada",
  ],
  priceRange: "Free",
};

/**
 * Per-page SEO configuration
 * Each page has optimized title, description, and keywords for Calgary/Alberta market
 */
export const PAGE_META: Record<string, PageMeta> = {
  // Landing Page (160 chars max)
  "/": {
    title:
      "Builder Buddy - AI Building Code Assistant for Calgary & Alberta | NBC 2023",
    description:
      "Free AI building code assistant for Calgary & Alberta. Instant answers on NBC 2023, permits, zoning, and construction costs. Trusted by Calgary pros.",
    keywords: [
      "Calgary building code",
      "Alberta building code",
      "NBC 2023",
      "Calgary permits",
      "building permit Calgary",
      "Alberta NBC",
      "Calgary construction",
      "Calgary contractor",
      "building code assistant",
      "Calgary zoning",
      "Calgary builder tools",
      "Calgary general contractor",
      "CCA Calgary",
      "BILD Calgary Region",
      "Calgary developer tools",
      "permit expediter Calgary",
      "construction manager Calgary",
    ],
    canonicalPath: "/",
    ogType: "website",
    structuredDataType: "WebApplication",
  },

  // AI Chat Page (160 chars max)
  "/chat": {
    title:
      "AI Building Code Chat - Ask Questions About Calgary Building Codes | Builder Buddy",
    description:
      "Ask Calgary building code questions and get instant AI answers. Covers NBC 2023, Part 9 residential, fire separation, egress windows, and more. Free.",
    keywords: [
      "ask building code questions",
      "Calgary building code help",
      "NBC questions",
      "building code AI",
      "stair width Alberta",
      "egress window Calgary",
      "ceiling height Alberta",
      "fire separation requirements",
      "building code Part 9",
    ],
    canonicalPath: "/chat",
    structuredDataType: "FAQPage",
  },

  // Code Library / Explore Page (160 chars max)
  "/explore": {
    title:
      "Calgary Building Code Library - Search NBC, NPC, NFC, NECB | Builder Buddy",
    description:
      "Search Calgary's complete building code library. NBC 2023 Alberta Edition, plumbing, fire, energy codes, and Land Use Bylaw 1P2007. Free access.",
    keywords: [
      "Calgary building code search",
      "NBC 2023 search",
      "National Building Code Alberta",
      "NPC plumbing code",
      "NFC fire code",
      "NECB energy code",
      "Calgary Land Use Bylaw",
      "building code articles",
      "code library",
    ],
    canonicalPath: "/explore",
    structuredDataType: "WebApplication",
  },

  // Permits Page (160 chars max)
  "/permits": {
    title:
      "Calgary Building Permit Requirements Checker - Costs & Timeline | Builder Buddy",
    description:
      "Check Calgary building permit requirements, costs, and timelines. Covers basement development, secondary suites, decks, and commercial projects. Free.",
    keywords: [
      "Calgary building permit",
      "building permit cost Calgary",
      "Calgary permit requirements",
      "development permit Calgary",
      "Calgary basement permit",
      "secondary suite permit Calgary",
      "Calgary renovation permit",
      "permit application Calgary",
      "how long Calgary permit",
      "permit expediter Calgary",
      "repeat permit Calgary",
      "contractor permit Calgary",
    ],
    canonicalPath: "/permits",
    structuredDataType: "FAQPage",
  },

  // Permit Info Page (160 chars max)
  "/permits/info": {
    title:
      "Calgary Building Permit Guide - Complete Requirements & Process | Builder Buddy",
    description:
      "Complete Calgary building permit guide. Permit types, fees, inspections, and application steps for residential and commercial projects in Alberta.",
    keywords: [
      "Calgary permit guide",
      "building permit process Calgary",
      "Calgary permit fees",
      "Calgary inspection requirements",
      "permit types Calgary",
      "Calgary building inspection",
    ],
    canonicalPath: "/permits/info",
    structuredDataType: "WebPage",
  },

  // SDAB Expert Page (160 chars max)
  "/sdab": {
    title:
      "Calgary SDAB Expert - Development Appeal Risk Assessment | Builder Buddy",
    description:
      "AI-powered SDAB appeal risk assessment for Calgary. Analyze variances, setback relaxations, and get success probability with similar case outcomes.",
    keywords: [
      "Calgary SDAB",
      "SDAB appeal Calgary",
      "setback relaxation Calgary",
      "development appeal Calgary",
      "Calgary variance",
      "land use amendment Calgary",
      "SDAB success rate",
      "Calgary development permit appeal",
      "R-CG zoning Calgary",
      "Calgary developer permit appeal",
      "builder variance Calgary",
    ],
    canonicalPath: "/sdab",
    structuredDataType: "WebApplication",
  },

  // Quantity Survey Page (160 chars max)
  "/quantity-survey": {
    title:
      "Calgary Construction Cost Estimator & Quantity Survey Calculator | Builder Buddy",
    description:
      "Free Calgary quantity survey and cost calculator. Accurate building costs per sq ft, material estimates, and project budgets. CIQS methodology. Free.",
    keywords: [
      "Calgary construction cost",
      "construction cost estimator Calgary",
      "building cost per square foot Calgary",
      "quantity survey Calgary",
      "Calgary construction estimate",
      "cost to build house Calgary",
      "Calgary renovation cost",
      "basement development cost Calgary",
      "Calgary builder cost calculator",
      "RSMeans Calgary",
      "construction budget Calgary",
      "material cost Calgary",
      "Alberta construction cost",
      "Calgary infill cost",
      "commercial construction cost Calgary",
      "Calgary contractor estimate",
      "builder cost calculator Calgary",
      "construction budget Alberta",
    ],
    canonicalPath: "/quantity-survey",
    structuredDataType: "WebApplication",
  },

  // Feasibility Study Page (160 chars max)
  "/feasibility": {
    title:
      "Calgary Development Feasibility Calculator - ROI & Construction Cost Analysis | Builder Buddy",
    description:
      "Calgary construction feasibility calculator. Compare material sourcing, calculate ROI, find Alberta incentives, and optimize project timelines. Free.",
    keywords: [
      "Calgary development feasibility",
      "construction ROI Calgary",
      "Calgary development calculator",
      "building cost analysis Calgary",
      "Calgary construction investment",
      "Alberta construction incentives",
      "Calgary material costs",
      "construction feasibility study Calgary",
      "Calgary development costs",
      "is building worth it Calgary",
      "Calgary new home cost",
      "Calgary infill development",
      "secondary suite ROI Calgary",
      "multi-family development Calgary",
      "renovation vs new build Calgary",
      "Calgary construction timeline",
      "Alberta building grants",
      "tariff impact construction Canada",
      "Calgary real estate investment",
      "small investor Calgary construction",
      "development ROI Calgary",
      "Calgary land development",
    ],
    canonicalPath: "/feasibility",
    structuredDataType: "WebApplication",
  },

  // Privacy Policy Page
  "/privacy": {
    title: "Privacy Policy | Builder Buddy - Building Code Assistant",
    description:
      "Privacy policy for Builder Buddy, the AI-powered building code assistant for Calgary and Alberta. Learn how we protect your data and privacy.",
    keywords: ["privacy policy", "data protection", "Builder Buddy privacy"],
    canonicalPath: "/privacy",
    structuredDataType: "WebPage",
  },

  // Terms of Service Page
  "/terms": {
    title: "Terms of Service | Builder Buddy - Building Code Assistant",
    description:
      "Terms of service for Builder Buddy, the AI-powered building code assistant. Usage terms, disclaimers, and conditions for using our Calgary building code tools.",
    keywords: [
      "terms of service",
      "terms and conditions",
      "Builder Buddy terms",
    ],
    canonicalPath: "/terms",
    structuredDataType: "WebPage",
  },

  // Blog Listing Page (160 chars max)
  "/blog": {
    title:
      "Builder Buddy Blog - Calgary Building Code Tips & Guides | Expert Advice",
    description:
      "Expert Calgary building code articles, permit guides, and construction tips. Updated with NBC 2023 Alberta Edition insights and cost analysis.",
    keywords: [
      "Calgary building code blog",
      "construction tips Calgary",
      "building permit guide",
      "Calgary development news",
      "NBC 2023 updates",
      "Calgary contractor advice",
    ],
    canonicalPath: "/blog",
    ogType: "website",
    structuredDataType: "WebPage",
  },
};

/**
 * Get page meta by path, with fallback to home page
 */
export function getPageMeta(path: string): PageMeta {
  // Handle dynamic routes
  if (path.startsWith("/blog/") && path !== "/blog") {
    // Blog post pages - will be handled by BlogPostPage with article-specific meta
    return {
      title: "Blog Post | Builder Buddy",
      description:
        "Read expert articles about Calgary building codes and construction.",
      keywords: ["Calgary building code", "construction blog"],
      canonicalPath: path,
      ogType: "article",
      structuredDataType: "Article",
    };
  }

  if (path.startsWith("/permits/") && !path.includes("info")) {
    // Permit detail pages - not indexed
    return {
      title: "Permit Application | Builder Buddy",
      description: "View and manage your building permit application.",
      keywords: ["building permit", "permit application"],
      canonicalPath: path,
      structuredDataType: "WebPage",
    };
  }

  return PAGE_META[path] || PAGE_META["/"];
}
